import {
    ExternalLinkInIframeHandlerBase,
    ExternalLinkInIframeRendererParams
} from "../external-link-in-iframe-handler-base";
import {
    IPendingActionExternalLinkHandler, PendingActionData, PendingActionReasonEnum,
    PendingActionResultEnum
} from "./pending-action.external-link-handler.interface";
import {ExternalLinksZIndexes} from "../../external-links-z-indexes";
import {CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";
import {
    AreaType,
    ControlCenterDeeplink,
    ControlCenterOpenSource,
    IChannel,
    IControlCenterFullSpecificParams
} from "@sparkware/uc-sdk-core";
import {Nullable, NullableUndefinedString} from "../../../../../common/types/nullable-types";
import {UserController_GetPendingActionsRequest} from "../../../api/api.service";
import {CIPropRead} from "../../../../../utils/case-insensitive-property-reader";

const PENDING_ACTION_POST_MESSAGE = {
    ControlCenter: '101',
    Pending: 'pending'
};


export abstract class PendingActionExternalLinkHandler<TOptions> extends ExternalLinkInIframeHandlerBase implements IPendingActionExternalLinkHandler<TOptions> {

    private _pendingActionResult: PendingActionResultEnum = PendingActionResultEnum.BlockCurrentFlow;
    private _currentPendingAction: Nullable<PendingActionData> = null;

    protected abstract _createPendingActionsRequest(): UserController_GetPendingActionsRequest;
    protected abstract _getGnwpUrl(pendingActionData: PendingActionData, options?: TOptions): Promise<string>;

    async openPendingAction(options?: TOptions): Promise<PendingActionResultEnum> {
        return await this._executePendingActions(options);
    }

    protected async _getUserPendingActions(request: UserController_GetPendingActionsRequest): Promise<PendingActionData[]> {
        if(!this.services.user.isAuthorized) {
            return [];
        }

        try {
            const response = await this.services.api.userController.getPendingActions(request);
            //sort pending actions ascending by priority
            return response.PendingActions.sort((pa1, pa2) => {
                const priority1 = pa1.Priority ?? 99999999;
                const priority2 = pa2.Priority ?? 99999999;
                return priority1 - priority2;
            });
        } catch (err) {
            this.services.logger.error(`Failed to get user pending actions for ${this.services.user.cid}`, err);
            return [];
        }

    }

    protected async _executePendingActions(options?: TOptions, previousPendingAction?: PendingActionData): Promise<PendingActionResultEnum> {

        const pendingActions = await this._getUserPendingActions(this._createPendingActionsRequest());

        if(pendingActions.length === 0) {
            return PendingActionResultEnum.ContinueCurrentFlow;
        }

        if(pendingActions.some(pa => pa.Id === previousPendingAction?.Id)) {
            //it means that the pending action that we just show is still present, so we return BlockCurrentFlow to avoid entering an infinite loop
            return PendingActionResultEnum.BlockCurrentFlow;
        }

        await this._showPendingAction(pendingActions[0], options);

        if(this._pendingActionResult !== PendingActionResultEnum.ContinueCurrentFlow) {
            return this._pendingActionResult;
        }

        return this._executePendingActions(options, pendingActions[0]);
    }

    protected _getPendingActionIframeRendererSpecificParams(pendingActionData: PendingActionData): ExternalLinkInIframeRendererParams {
        let specificParams: Partial<ExternalLinkInIframeRendererParams> = {};
        if(pendingActionData.ReasonId === PendingActionReasonEnum.GamingCautionInteraction) {
            specificParams.hideCloseButton = true;
            specificParams.maxWidth = 700;
            specificParams.maxHeight = 500;
        }

        return {
            ...this._getIframeRendererParams(),
            ...specificParams
        };
    }

    protected async _showPendingAction(pendingActionData: PendingActionData, options?: TOptions): Promise<void> {
        this._pendingActionResult = PendingActionResultEnum.BlockCurrentFlow;
        this._currentPendingAction = pendingActionData;
        const url = await this._getGnwpUrl(pendingActionData, options);
        await this._showExternalLinkDialog(url, this._getPendingActionIframeRendererSpecificParams(pendingActionData));
    }

    protected get _hideCloseButton(): boolean {
        return false;
    }

    protected _getIframeRendererParams(): ExternalLinkInIframeRendererParams {
        return {
            maxWidth: 1014,
            maxHeight: 702,
            zIndex: ExternalLinksZIndexes.PendingAction,
            httpVerb: 'POST',
            analyticsAction: this._getGnwpSource(),
            hideCloseButton: this._hideCloseButton
        };
    }

    protected async _onPostMessageReceived(message: MessageEvent): Promise<void> {
        const actionId = this._extractActionId(message);

        if(!actionId) {
            return;
        }

        const msgData = this._parseMessageData(message);

        switch (actionId.toLowerCase()) {
            case PENDING_ACTION_POST_MESSAGE.ControlCenter:
                const channel = CIPropRead.getProperty('Channel', msgData);
                const appSpecificParameters = CIPropRead.getProperty('AppSpecificParameters', msgData);

                await this._openControlCenter({channel: channel, appSpecificParameters: appSpecificParameters});
                break;
            case PENDING_ACTION_POST_MESSAGE.Pending:
                await this._onPending(CIPropRead.getStringProperty('Token', msgData));
                break;
        }
    }

    protected async _openControlCenter(options: {channel: IChannel; appSpecificParameters: Partial<IControlCenterFullSpecificParams>;}): Promise<void> {
        this._closeExternalLinkDialog();
        await (await this.services.appLauncher).openControlCenterFull({
            channel: {
                source: CHANNEL_SOURCE.pendingAction,
                area: AreaType.button,
                ...options.channel
            },
            businessCorrelationID: this.services.tools.getGuid(),
            appSpecificParameters: {
                source: ControlCenterOpenSource.Lobby,
                isIncreaseLimitsDisabled: true,
                deeplink: ControlCenterDeeplink.Main,
                ...options.appSpecificParameters
            }
        })
    }

    protected async _onPending(token: NullableUndefinedString): Promise<void> {
        if(token) {
            //if we have a token it means that the pending action was resolved, so we can continue the current flow
            this._pendingActionResult = PendingActionResultEnum.ContinueCurrentFlow;
        }
        this._closeExternalLinkDialog();

    }


}